import type { Dispatch } from '@reduxjs/toolkit';
import { homeActions } from 'app/pages/Home/slice';
import { saveUserPreferences } from 'utils/helpers';
import type { ViewStateParams } from 'utils/viewStateDeepLink';

type CategoryToProductMap = Record<string, string[]>;

/**
 * Apply Must Wins view + dropdown filter state from deep-link params.
 * Shared by initial URL load, SPA saved-view navigation, and useMustWinDeepLink.
 */
export function applyMustWinViewStateFromParams(
  dispatch: Dispatch,
  params: Pick<
    ViewStateParams,
    | 'mustWin'
    | 'mustWinType'
    | 'mustWinValue'
    | 'mustWinLabel'
    | 'mustWinFilters'
  >,
  categoryToProductMap: CategoryToProductMap = {},
  options: { persistPreferences?: boolean } = {}
) {
  const persistPreferences = options.persistPreferences !== false;

  const persist = (prefs: Parameters<typeof saveUserPreferences>[0]) => {
    if (persistPreferences) {
      saveUserPreferences(prefs);
    }
  };
  if (params.mustWin === false) {
    dispatch(homeActions.setMustWinView(false));
    dispatch(homeActions.setMustWinDropdownSelection(null));
    dispatch(homeActions.setHideEmptySwimLanes(false));
    persist({
      mustWinViewActive: false,
      mustWinDropdownSelection: null,
      hideEmptySwimLanes: false
    });
    return;
  }

  if (!params.mustWin) {
    return;
  }

  const hasDropdownSelection =
    Boolean(params.mustWinValue) &&
    Boolean(params.mustWinLabel) &&
    (Boolean(params.mustWinType) || Boolean(params.mustWinFilters));

  if (!hasDropdownSelection) {
    dispatch(homeActions.setMustWinView(true));
    persist({ mustWinViewActive: true });
    return;
  }

  dispatch(homeActions.setMustWinView(true));
  dispatch(homeActions.setHideEmptySwimLanes(true));
  dispatch(homeActions.setMustWinDropdownSelection(params.mustWinValue!));

  persist({
    mustWinViewActive: true,
    hideEmptySwimLanes: true,
    mustWinDropdownSelection: params.mustWinValue!
  });

  dispatch(homeActions.setSelectedAudiences([]));
  dispatch(homeActions.setSelectedCats([]));
  dispatch(homeActions.setSelectedProducts([]));
  dispatch(
    homeActions.setCategoryProductSelection({
      categories: [],
      products: []
    })
  );

  if (params.mustWinFilters) {
    const audiences = params.mustWinFilters.audiences || [];
    const categories = params.mustWinFilters.categories || [];
    const products = params.mustWinFilters.products || [];
    const productGroups = params.mustWinFilters.productGroups || [];

    if (audiences.length > 0) {
      dispatch(homeActions.setSelectedAudiences(audiences));
    }

    if (productGroups.length > 0) {
      dispatch(homeActions.setSelectedGroups(productGroups));
    }

    let allProducts = [...products];
    if (categories.length > 0) {
      categories.forEach(category => {
        const childProducts = categoryToProductMap[category] || [];
        if (childProducts.length > 0) {
          allProducts.push(...childProducts);
        }
      });
      dispatch(
        homeActions.setCategoryProductSelection({
          categories,
          products: allProducts
        })
      );
      dispatch(homeActions.setSelectedCats(categories));
    }

    if (allProducts.length > 0) {
      allProducts = Array.from(new Set(allProducts));
      dispatch(homeActions.setSelectedProducts(allProducts));
    }

    persist({
      audiences,
      categories,
      products: allProducts,
      productGroups,
      categoryProductCategories: categories,
      categoryProductProducts: allProducts
    });
    return;
  }

  switch (params.mustWinType) {
    case 'audience':
      dispatch(homeActions.setSelectedAudiences([params.mustWinValue!]));
      persist({
        audiences: [params.mustWinValue!],
        categories: [],
        products: [],
        categoryProductCategories: [],
        categoryProductProducts: []
      });
      break;
    case 'productGroup':
      dispatch(homeActions.setSelectedGroups([params.mustWinValue!]));
      persist({
        productGroups: [params.mustWinValue!],
        audiences: [],
        categories: [],
        products: [],
        categoryProductCategories: [],
        categoryProductProducts: []
      });
      break;
    case 'category': {
      const childProducts = categoryToProductMap[params.mustWinValue!] || [];
      if (childProducts.length > 0) {
        dispatch(
          homeActions.setCategoryProductSelection({
            categories: [params.mustWinValue!],
            products: childProducts
          })
        );
        dispatch(homeActions.setSelectedCats([params.mustWinValue!]));
        dispatch(homeActions.setSelectedProducts(childProducts));
        persist({
          categoryProductCategories: [params.mustWinValue!],
          categoryProductProducts: childProducts,
          categories: [params.mustWinValue!],
          products: childProducts,
          audiences: []
        });
      } else {
        dispatch(
          homeActions.setCategoryProductSelection({
            categories: [params.mustWinValue!],
            products: []
          })
        );
        dispatch(homeActions.setSelectedCats([params.mustWinValue!]));
        persist({
          categoryProductCategories: [params.mustWinValue!],
          categoryProductProducts: [],
          categories: [params.mustWinValue!],
          audiences: [],
          products: []
        });
      }
      break;
    }
    case 'product':
      dispatch(
        homeActions.setCategoryProductSelection({
          categories: [],
          products: [params.mustWinValue!]
        })
      );
      dispatch(homeActions.setSelectedProducts([params.mustWinValue!]));
      persist({
        categoryProductCategories: [],
        categoryProductProducts: [params.mustWinValue!],
        products: [params.mustWinValue!],
        audiences: [],
        categories: []
      });
      break;
    default:
      break;
  }
}
